import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Loader2, SlidersHorizontal, RotateCcw, Save } from "lucide-react";

const SENS = [
  { key: "avail_attack_sens", label: "Missing attackers", help: "How hard players out cut a team's goals", min: 0, max: 3, step: 0.1, toggle: "availability" },
  { key: "avail_def_sens", label: "Missing defenders", help: "How hard players out let the opponent score", min: 0, max: 3, step: 0.1, toggle: "availability" },
  { key: "gk_weight", label: "Keeper weight", help: "Extra weight when a first-choice keeper is out", min: 1, max: 3, step: 0.1, toggle: "availability" },
  { key: "fatigue_sens", label: "Rest & congestion", help: "How hard short rest / fixture pile-ups lower goals", min: 0, max: 3, step: 0.1, toggle: "rest" },
  { key: "motivation_sens", label: "Motivation (late season)", help: "How hard title / relegation stakes swing goals", min: 0, max: 3, step: 0.1, toggle: "motivation" },
  { key: "formation_sens", label: "Formation tilt", help: "How hard an attacking XI shape lifts goals", min: 0, max: 3, step: 0.1, toggle: "formation" },
];

const TOGGLES = [
  { key: "availability", label: "Injuries, suspensions & lineups" },
  { key: "rest", label: "Rest & fixture congestion" },
  { key: "motivation", label: "Standings / motivation" },
  { key: "formation", label: "Formation" },
];

export default function AdminModelTuning() {
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/model-tuning")
      .then((r) => setT(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => load(), [load]);

  const setSens = (key, v) => setT((p) => ({ ...p, [key]: v }));
  const setToggle = (key, v) => setT((p) => ({ ...p, enabled: { ...p.enabled, [key]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/model-tuning", t);
      setT(data);
      toast.success("Model sensitivities saved. New analyses use these right away.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/model-tuning/reset");
      setT(data);
      toast.success("Reset to defaults.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !t) {
    return (
      <div className="h-60 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-model-tuning-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2">
          <SlidersHorizontal className="h-8 w-8 text-primary" /> Model Tuning
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Dial how hard each signal swings a match prediction. 1.0 is the default strength,
          0 turns a signal off, higher makes it push harder. Changes apply to the next analysis.
        </p>
      </div>

      <div className="glass rounded-3xl p-5 space-y-6" data-testid="tuning-sliders">
        {SENS.map((s) => {
          const on = t.enabled?.[s.toggle] !== false;
          return (
            <div key={s.key} className={on ? "" : "opacity-40"} data-testid={`tuning-row-${s.key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div>
                  <div className="font-semibold text-sm">{s.label}</div>
                  <div className="text-xs text-muted-foreground">{s.help}</div>
                </div>
                <span className="font-display font-bold text-primary tabular-nums w-10 text-right"
                      data-testid={`tuning-value-${s.key}`}>
                  {Number(t[s.key]).toFixed(1)}
                </span>
              </div>
              <Slider
                value={[Number(t[s.key])]}
                min={s.min} max={s.max} step={s.step}
                disabled={!on}
                onValueChange={(v) => setSens(s.key, v[0])}
                data-testid={`tuning-slider-${s.key}`}
              />
            </div>
          );
        })}
      </div>

      <div className="glass rounded-3xl p-5 space-y-4" data-testid="tuning-toggles">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
          Turn signals on / off
        </div>
        {TOGGLES.map((tg) => (
          <div key={tg.key} className="flex items-center justify-between">
            <span className="text-sm font-medium">{tg.label}</span>
            <Switch
              checked={t.enabled?.[tg.key] !== false}
              onCheckedChange={(v) => setToggle(tg.key, v)}
              data-testid={`tuning-toggle-${tg.key}`}
            />
          </div>
        ))}
      </div>

      <Separator />

      <div className="flex items-center gap-3">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="tuning-save-btn"
          className="rounded-full font-semibold gap-2"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save changes
        </Button>
        <Button
          variant="outline"
          onClick={reset}
          disabled={saving}
          data-testid="tuning-reset-btn"
          className="rounded-full gap-2"
        >
          <RotateCcw className="h-4 w-4" /> Reset to defaults
        </Button>
      </div>
    </div>
  );
}
